import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';

import {
  TALENT_DISCOVERY_BY_ID_V3,
  TALENT_DISCOVERY_DATA_V3,
  type TalentDiscoveryRespUserV3,
} from '@revelio/data-access';

import { transformByIdUserToProfileData } from '../../CustomDash/components/Chart/FullProfileInfo/transformTableRowToProfileData';
import type { FullProfileInfoData } from '../../CustomDash/components/Chart/FullProfileInfo/types';
import {
  buildTxtNode,
  wrapExprNode,
} from '../../CustomDash/components/DataProvider/talentDiscovery/node-builders';
import { TalentDiscoveryV3Tags } from '../../CustomDash/components/DataProvider/talentDiscovery/types';

interface UseNameSearchResult {
  searchUser: TalentDiscoveryRespUserV3 | null;
  profileData: FullProfileInfoData | null;
  isSearching: boolean;
  isLoadingProfile: boolean;
  error: string | null;
}

export const useNameSearch = (
  searchName: string,
  isActive: boolean
): UseNameSearchResult => {
  const [searchUser, setSearchUser] =
    useState<TalentDiscoveryRespUserV3 | null>(null);
  const [profileData, setProfileData] = useState<FullProfileInfoData | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Step 1: Search for users by name
  const searchVariables = useMemo(() => {
    if (!searchName || !isActive) return undefined;

    const expr = wrapExprNode(
      buildTxtNode(TalentDiscoveryV3Tags.Name, searchName)
    );

    return {
      query: {
        filters: {
          expr,
          roles_tax: 'default',
          skills_tax: 0,
        },
        pagination: { page_num: 1, page_size: 1 },
      },
    };
  }, [searchName, isActive]);

  const [{ data: searchData, fetching: isSearching, error: searchError }] =
    useQuery({
      query: TALENT_DISCOVERY_DATA_V3,
      variables: searchVariables,
      pause: !searchVariables,
    });

  // Extract the first matching user
  useEffect(() => {
    if (searchError) {
      setError('Failed to search for profiles');
      return;
    }

    const users = searchData?.talent_discovery_search_v3?.users;
    if (users && users.length > 0) {
      const firstUser = users[0];
      if (firstUser) {
        setSearchUser(firstUser as TalentDiscoveryRespUserV3);
        setError(null);
      } else {
        setError('No profile found for this name');
      }
    } else if (searchData && !isSearching) {
      setError('No profile found for this name');
    }
  }, [searchData, searchError, isSearching]);

  // Step 2: Fetch full profile by ID
  const profileVariables = useMemo(() => {
    if (!searchUser?.original_user_id) return undefined;

    return {
      query: {
        filters: {
          roles_tax: 'default',
          skills_tax: 0,
          // CHANGE 2: dual-send new int taxonomy ids (default taxonomy = 0).
          roles_tax_id: 0,
          skills_tax_id: 0,
        },
        original_users_ids: [searchUser.original_user_id],
      },
    };
  }, [searchUser]);

  const [
    { data: profileByIdData, fetching: isLoadingProfile, error: profileError },
  ] = useQuery({
    query: TALENT_DISCOVERY_BY_ID_V3,
    variables: profileVariables,
    pause: !profileVariables,
  });

  // Transform profile data
  useEffect(() => {
    if (profileError) {
      setError('Failed to load profile details');
      return;
    }

    const users = profileByIdData?.talent_discovery_search_by_id_v3?.users;
    if (users && users.length > 0) {
      const user = users[0];
      if (user) {
        const transformed = transformByIdUserToProfileData(user);
        setProfileData(transformed);
      }
    }
  }, [profileByIdData, profileError]);

  return {
    searchUser,
    profileData,
    isSearching,
    isLoadingProfile,
    error,
  };
};
